import { useState, useCallback, useEffect } from 'react';
import {
  collection,
  doc,
  addDoc,
  updateDoc,
  getDoc,
  getDocs,
  query,
  orderBy,
  limit,
  writeBatch,
  onSnapshot,
  Timestamp
} from 'firebase/firestore';
import { db } from '../lib/firebase';
import { ImpostorRound, Player, ImpostorVote } from '../types';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';

export const useImpostor = (roomCode: string | null) => {
  const [loading, setLoading] = useState(false);
  const [currentImpostorRound, setCurrentImpostorRound] = useState<ImpostorRound | null>(null);
  const [hasVoted, setHasVoted] = useState(false);
  const { user } = useAuth();
  const { showError, showSuccess } = useToast();

  // Subscribe to current impostor round
  useEffect(() => {
    if (!roomCode) {
      setCurrentImpostorRound(null);
      return;
    }

    const roundsRef = collection(db, 'rooms', roomCode, 'impostorRounds');
    const currentRoundQuery = query(
      roundsRef,
      orderBy('index', 'desc'),
      limit(1)
    );

    const unsubscribe = onSnapshot(
      currentRoundQuery,
      (snapshot) => {
        if (!snapshot.empty) {
          const roundDoc = snapshot.docs[0];
          const roundData = roundDoc.data();

          // Normalize old format to new format for backward compatibility
          const round: ImpostorRound = {
            id: roundDoc.id,
            ...roundData,
            // Convert old impostorId (string) to new impostorIds (array)
            impostorIds: roundData.impostorIds || (roundData.impostorId ? [roundData.impostorId] : []),
            // Ensure impostorsFound exists
            impostorsFound: roundData.impostorsFound || []
          } as ImpostorRound;

          setCurrentImpostorRound(round);

          // Check if user has voted in this round
          if (user?.uid) {
            checkIfVoted(roomCode, round.id, user.uid);
          }
        } else {
          setCurrentImpostorRound(null);
        }
      },
      (error) => {
        console.error('Error listening to impostor round:', error);
        showError('Error al obtener la ronda actual');
      }
    );

    return unsubscribe;
  }, [roomCode, user?.uid, showError]);

  const checkIfVoted = async (roomCode: string, roundId: string, userId: string) => {
    try {
      const votesRef = collection(db, 'rooms', roomCode, 'impostorRounds', roundId, 'votes');
      const votesSnap = await getDocs(votesRef);
      const userVoted = votesSnap.docs.some(doc => doc.data().voterId === userId);
      setHasVoted(userVoted);
    } catch (error) {
      console.error('Error checking if voted:', error);
    }
  };

  const submitVote = useCallback(async (
    roomCode: string,
    roundId: string,
    accusedId: string
  ): Promise<void> => {
    if (!user?.uid) throw new Error('Usuario no autenticado');

    setLoading(true);
    try {
      console.log('🗳️ Submitting impostor vote:', {
        roomCode,
        roundId,
        voterId: user.uid,
        accusedId
      });

      // Don't accept a vote once the round has already moved past PLAYING
      // (another player's accusation ended it first).
      if (currentImpostorRound && currentImpostorRound.id === roundId && currentImpostorRound.status !== 'PLAYING') {
        throw new Error('La ronda ya ha terminado');
      }

      // Check if user already voted
      const votesRef = collection(db, 'rooms', roomCode, 'impostorRounds', roundId, 'votes');
      const votesSnap = await getDocs(votesRef);

      if (votesSnap.docs.some(doc => doc.data().voterId === user.uid)) {
        throw new Error('Ya has votado en esta ronda');
      }

      // Add vote
      const voteData: Omit<ImpostorVote, 'id'> = {
        voterId: user.uid,
        accusedId,
        roundId,
        submittedAt: Timestamp.now()
      };

      await addDoc(votesRef, voteData);

      // Update round with vote info
      const roundRef = doc(db, 'rooms', roomCode, 'impostorRounds', roundId);
      const round = currentImpostorRound;

      if (!round) {
        throw new Error('Ronda no encontrada');
      }

      const numImpostors = round.impostorIds.length;
      const isImpostorAccused = round.impostorIds.includes(accusedId);

      // Case 1: Single impostor game
      if (numImpostors === 1) {
        const result = isImpostorAccused ? 'BOTH_IMPOSTORS_CAUGHT' : 'IMPOSTORS_ESCAPED';

        await updateDoc(roundRef, {
          status: 'REVEAL',
          voterId: user.uid,
          accusedId,
          result
        });

        const roomRef = doc(db, 'rooms', roomCode);
        await updateDoc(roomRef, {
          status: 'IMPOSTOR_REVEAL'
        });

        console.log('✅ Single impostor vote submitted:', result);
      }
      // Case 2: Two impostors game
      else {
        if (isImpostorAccused) {
          // Correct accusation - move to first reveal phase
          await updateDoc(roundRef, {
            status: 'FIRST_IMPOSTOR_REVEAL',
            voterId: user.uid,
            accusedId,
            impostorsFound: [accusedId]
          });

          const roomRef = doc(db, 'rooms', roomCode);
          await updateDoc(roomRef, {
            status: 'IMPOSTOR_FIRST_REVEAL'
          });

          console.log('✅ First impostor found:', accusedId);
        } else {
          // Wrong accusation - game over, impostors escaped
          await updateDoc(roundRef, {
            status: 'REVEAL',
            voterId: user.uid,
            accusedId,
            result: 'IMPOSTORS_ESCAPED'
          });

          const roomRef = doc(db, 'rooms', roomCode);
          await updateDoc(roomRef, {
            status: 'IMPOSTOR_REVEAL'
          });

          console.log('❌ Wrong accusation, both impostors escaped');
        }
      }

      console.log('✅ Vote submitted successfully');
      setHasVoted(true);
    } catch (error: any) {
      console.error('❌ Error submitting vote:', error);
      showError(error.message || 'Error al enviar el voto');
      throw error;
    } finally {
      setLoading(false);
    }
  }, [user, currentImpostorRound, showError]);

  const nextRound = useCallback(async (roomCode: string): Promise<void> => {
    if (!user?.uid) throw new Error('Usuario no autenticado');

    setLoading(true);
    try {
      console.log('➡️ Advancing to next impostor round');

      // Get room data (single doc read, not a full-collection scan)
      const roomDoc = await getDoc(doc(db, 'rooms', roomCode));
      const roomData = roomDoc.data();
      const impostorMinutes = roomData?.settings?.impostorMinutes || 3; // Get configured minutes
      const adminMode = roomData?.settings?.impostorAdminMode || false; // Check admin mode

      const roomSnap = await getDocs(query(collection(db, 'rooms', roomCode, 'impostorRounds'), orderBy('index')));
      const currentRoundIndex = roomSnap.size - 1;
      const nextRoundIndex = currentRoundIndex + 1;

      // Get current players
      const playersRef = collection(db, 'rooms', roomCode, 'players');
      const playersSnap = await getDocs(playersRef);
      const players = playersSnap.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Player[];

      const connectedPlayers = players.filter(p => p.connected);

      // Load impostor words
      const impostorWordsModule = await import('../../data/impostor-words.json');
      const allWords = impostorWordsModule.default;

      // Decide if this round uses a player name
      // ADMIN MODE: 80% chance | NORMAL MODE: 30% chance
      const playerNameChance = adminMode ? 0.8 : 0.3;
      const usePlayerName = Math.random() <= playerNameChance;

      if (adminMode) {
        console.log('🎭 ADMIN MODE - Using', playerNameChance * 100 + '% chance for player names');
      }

      let selectedWord: string;
      let wordId: string;

      if (usePlayerName) {
        // Pick a random player's name (can be anyone, including impostor)
        const randomPlayer = connectedPlayers[Math.floor(Math.random() * connectedPlayers.length)];
        selectedWord = randomPlayer.nickname || 'Jugador';
        wordId = `player_${randomPlayer.id}`;
        console.log('🎭 Using player name as word:', selectedWord);
      } else {
        // Select random word (different from previous)
        const previousWord = currentImpostorRound?.word;
        const availableWords = allWords.filter(w => w.word !== previousWord);
        const randomWord = availableWords[Math.floor(Math.random() * availableWords.length)];
        selectedWord = randomWord.word;
        wordId = randomWord.id;
      }

      // Determine number of impostors based on player count
      const numImpostors = connectedPlayers.length >= 6 ? 2 : 1;

      // Select random impostor(s) (different from previous)
      const previousImpostorIds = currentImpostorRound?.impostorIds || [];
      const availablePlayers = connectedPlayers.filter(p => !previousImpostorIds.includes(p.id));

      const selectedImpostors: string[] = [];
      const shuffledPlayers = [...availablePlayers].sort(() => Math.random() - 0.5);

      for (let i = 0; i < numImpostors && i < shuffledPlayers.length; i++) {
        selectedImpostors.push(shuffledPlayers[i].id);
      }

      // Select random first player
      const randomFirstPlayer = connectedPlayers[Math.floor(Math.random() * connectedPlayers.length)];

      console.log('🎲 Next round setup:', {
        word: selectedWord,
        wordType: usePlayerName ? 'player_name' : 'normal_word',
        numImpostors,
        impostorIds: selectedImpostors,
        firstPlayerId: randomFirstPlayer.id
      });

      const batch = writeBatch(db);

      // Update room
      const roomDocRef = doc(db, 'rooms', roomCode);
      batch.update(roomDocRef, {
        status: 'IMPOSTOR_PLAYING',
        currentRoundIndex: nextRoundIndex
      });

      // Create next round
      const roundRef = doc(collection(db, 'rooms', roomCode, 'impostorRounds'));
      const roundData = {
        index: nextRoundIndex,
        wordId: wordId,
        word: selectedWord,
        isPlayerName: usePlayerName, // Track if this is a player name
        impostorIds: selectedImpostors, // Array of impostor IDs
        impostorsFound: [], // Empty initially
        firstPlayerId: randomFirstPlayer.id,
        startedAt: Timestamp.now(),
        deadlineAt: Timestamp.fromMillis(Date.now() + impostorMinutes * 60 * 1000), // Use configured minutes
        status: 'PLAYING'
      };

      batch.set(roundRef, roundData);

      await batch.commit();

      console.log('✅ Next round started');
      showSuccess('¡Nueva ronda iniciada!');
      setHasVoted(false);
    } catch (error: any) {
      console.error('❌ Error advancing to next round:', error);
      showError(error.message || 'Error al avanzar a la siguiente ronda');
      throw error;
    } finally {
      setLoading(false);
    }
  }, [user, currentImpostorRound, showError, showSuccess]);

  const startSecondVote = useCallback(async (roomCode: string, roundId: string): Promise<void> => {
    if (!user?.uid) throw new Error('Usuario no autenticado');

    setLoading(true);
    try {
      console.log('🔍 Starting second vote phase');

      const roundRef = doc(db, 'rooms', roomCode, 'impostorRounds', roundId);

      // Get room settings for timer duration (single doc read)
      const roomDoc = await getDoc(doc(db, 'rooms', roomCode));
      const roomData = roomDoc.data();
      const impostorMinutes = roomData?.settings?.impostorMinutes || 3;
      const secondVoteMinutes = Math.max(2, Math.floor(impostorMinutes * 0.66)); // 2/3 of original time, min 2 min

      await updateDoc(roundRef, {
        status: 'SECOND_VOTE',
        deadlineAt: Timestamp.fromMillis(Date.now() + secondVoteMinutes * 60 * 1000)
      });

      const roomRef = doc(db, 'rooms', roomCode);
      await updateDoc(roomRef, {
        status: 'IMPOSTOR_SECOND_VOTE'
      });

      console.log('✅ Second vote phase started');
      setHasVoted(false); // Reset vote status for second vote
    } catch (error: any) {
      console.error('❌ Error starting second vote:', error);
      showError(error.message || 'Error al iniciar segunda votación');
      throw error;
    } finally {
      setLoading(false);
    }
  }, [user, showError]);

  const submitSecondVote = useCallback(async (
    roomCode: string,
    roundId: string,
    accusedId: string
  ): Promise<void> => {
    if (!user?.uid) throw new Error('Usuario no autenticado');

    setLoading(true);
    try {
      console.log('🗳️ Submitting second impostor vote:', {
        roomCode,
        roundId,
        voterId: user.uid,
        accusedId
      });

      const round = currentImpostorRound;
      if (!round) {
        throw new Error('Ronda no encontrada');
      }

      // Get the remaining impostor (the one not yet found)
      const remainingImpostorId = round.impostorIds.find(id => !round.impostorsFound.includes(id));

      if (!remainingImpostorId) {
        throw new Error('No hay impostor restante');
      }

      const isCorrect = accusedId === remainingImpostorId;
      const result = isCorrect ? 'BOTH_IMPOSTORS_CAUGHT' : 'ONE_IMPOSTOR_CAUGHT';

      const roundRef = doc(db, 'rooms', roomCode, 'impostorRounds', roundId);
      await updateDoc(roundRef, {
        status: 'REVEAL',
        secondVoterId: user.uid,
        secondAccusedId: accusedId,
        result
      });

      const roomRef = doc(db, 'rooms', roomCode);
      await updateDoc(roomRef, {
        status: 'IMPOSTOR_REVEAL'
      });

      console.log('✅ Second vote submitted:', result);
      setHasVoted(true);
    } catch (error: any) {
      console.error('❌ Error submitting second vote:', error);
      showError(error.message || 'Error al enviar el segundo voto');
      throw error;
    } finally {
      setLoading(false);
    }
  }, [user, currentImpostorRound, showError]);

  const endGame = useCallback(async (roomCode: string): Promise<void> => {
    if (!user?.uid) throw new Error('Usuario no autenticado');

    setLoading(true);
    try {
      console.log('🏁 Ending impostor game');

      const roomRef = doc(db, 'rooms', roomCode);
      await updateDoc(roomRef, {
        status: 'ENDED'
      });

      console.log('✅ Game ended');
      showSuccess('¡Juego terminado!');
    } catch (error: any) {
      console.error('❌ Error ending game:', error);
      showError(error.message || 'Error al terminar el juego');
      throw error;
    } finally {
      setLoading(false);
    }
  }, [user, showError, showSuccess]);

  return {
    loading,
    currentImpostorRound,
    hasVoted,
    submitVote,
    submitSecondVote,
    startSecondVote,
    nextRound,
    endGame
  };
};
